import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MainService } from '../../services/main.service';
import { GmsInfoDirective } from '../_shared/gms-info/gms-info.directive';
import { GmsExtension } from '../../models/gms/gms-extension';
import { GmsWidget } from '../../models/gms/gms-widget';
import { ExtensionsService } from '../../services/extensions.service';
import { GmsChartService } from '../../services/gms-chart.service';
import { AuthService } from '../../auth/auth.service';
import { ChartData } from '../../models/gms/chart-data';
import { Timeseries } from '../../models/gms/timeseries';
import { HighchartComponent } from '../highchart/highchart.component';

@Component({
    selector: 'app-widget',
    templateUrl: './widget.component.html',
    styleUrls: ['./widget.component.css'],
})
export class WidgetComponent extends GmsInfoDirective implements OnInit, OnDestroy {
    extensionName: string;
    widgetName: string;
    activeExtension: GmsExtension;
    activeWidget: GmsWidget;

    chart: any;
    chartData: ChartData;
    chartDateRange: UntypedFormGroup;
    chartDateMin: Date;
    chartDataMax: Date;
    updateFlag: boolean;
    @ViewChild(HighchartComponent) highchartComponent: HighchartComponent;

    startDate: Date;
    endDate: Date;
    zoomedStartDate: Date;
    zoomedEndDate: Date;

    showSpinner = false;
    currentlyUpdating = false;
    widgetChartSubscription: any;

    rpmMode = false;

    constructor(
        public main: MainService,
        public router: Router,
        private route: ActivatedRoute,
        public extensionsService: ExtensionsService,
        private gmsChartService: GmsChartService,
        private authService: AuthService,
        private fb: UntypedFormBuilder,
    ) {
        super(main, router);
        const currentDate = new Date();
        this.chartDateMin = this.authService.getEarliestDateAllowedForRole();
        this.chartDataMax = currentDate;

        this.chartDateRange = this.fb.group({
            start: [undefined, []],
            end: [undefined, []],
        });
    }

    routeParamSubscription: any;

    ngOnInit(): void {
        super.ngOnInit();
        this.routeParamSubscription = this.route.paramMap.subscribe(params => {
            if (this.widgetChartSubscription) {
                this.widgetChartSubscription.unsubscribe();
            }
            this.chart = undefined;
            this.currentlyUpdating = false;

            this.gmsId = params.get('id');
            this.extensionName = params.get('extensionName');
            this.widgetName = params.get('widgetName');

            // Defer setActiveById to avoid NG0100: mutating main.activeItem during an
            // in-progress change detection cycle causes the NavComponent's activeShip
            // binding to see inconsistent values between the initial check and the end.
            setTimeout(() => {
                this.main.setActiveById(this.gmsId);
                this.resolveExtensionAndWidget();
                this.setDefaultChartBasedOn24HoursBeforeLastConnected();
            });
        });
    }

    private resolveExtensionAndWidget() {
        if (!this.main.activeItem?.extensions) return;
        this.activeExtension = this.main.activeItem.extensions.find(e => e.extension === this.extensionName);
        if (this.activeExtension) {
            this.activeWidget = this.activeExtension.widgets?.find(w => w.widget === this.widgetName);
        }
    }

    setDefaultChartBasedOn24HoursBeforeLastConnected() {
        this.chart = undefined;
        if (!this.activeWidget) return;

        const widgetLastUpdatedStr = this.main.activeItem?.widgetDatasourceLastUpdated?.[this.activeWidget.source];
        const lastUpdated = widgetLastUpdatedStr
            ? new Date(widgetLastUpdatedStr)
            : (this.main.activeEngine?.lastUpdated ?? this.main.activeItem?.lastUpdated);
        if (lastUpdated) {
            const lastConnectedDate = new Date(lastUpdated);
            const defaultStartDate = new Date(
                lastConnectedDate.getFullYear(),
                lastConnectedDate.getMonth(),
                lastConnectedDate.getDate() - 1,
                lastConnectedDate.getHours(),
                lastConnectedDate.getMinutes(),
                lastConnectedDate.getSeconds(),
            );

            if (defaultStartDate > this.authService.getEarliestDateAllowedForRole()) {
                this.chartDateRange.patchValue({ start: defaultStartDate, end: lastConnectedDate });

                this.widgetChartSubscription = this.gmsChartService
                    .getWidgetChart(this.gmsId, this.activeWidget.source, this.activeWidget.values, this.activeWidget.timeReference, defaultStartDate, lastConnectedDate)
                    .subscribe(chartData => {
                        this.buildChart(chartData);
                    });
            } else {
                const chartData = new ChartData();
                chartData.timeseries = new Timeseries();
                this.buildChart(chartData);
            }
        }
    }

    dateRangeChange(dateRangeStart: HTMLInputElement, dateRangeEnd: HTMLInputElement) {
        if (dateRangeStart.value && dateRangeEnd.value) {
            const startDateWithoutTime = new Date(dateRangeStart.value);
            const endDateWithoutTime = new Date(dateRangeEnd.value);
            const startDate = new Date(Date.UTC(startDateWithoutTime.getFullYear(), startDateWithoutTime.getMonth(), startDateWithoutTime.getDate(), 0, 0, 0));
            const endDate = new Date(Date.UTC(endDateWithoutTime.getFullYear(), endDateWithoutTime.getMonth(), endDateWithoutTime.getDate(), 23, 59, 59));
            this.chartDateChange(startDate, endDate, false);
        }
    }

    chartDateChange(startDate: Date, endDate: Date, chartTypeChanged: boolean) {
        if (chartTypeChanged && this.chartData && this.startDate === startDate && this.endDate === endDate) {
            this.updateChartSeries(this.chartData, chartTypeChanged);
        } else {
            if (!this.currentlyUpdating) {
                this.currentlyUpdating = true;
                if (this.widgetChartSubscription) {
                    this.widgetChartSubscription.unsubscribe();
                }

                if (startDate === this.chartDateRange.get('start').value && endDate === this.chartDateRange.get('end').value) {
                    this.resetZoomedTimeRange();
                } else {
                    this.setZoomedTimeRange(startDate, endDate);
                }

                this.startDate = startDate;
                this.endDate = endDate;
                this.widgetChartSubscription = this.gmsChartService
                    .getWidgetChart(this.gmsId, this.activeWidget.source, this.activeWidget.values, this.activeWidget.timeReference, startDate, endDate)
                    .subscribe(
                        chartData => {
                            this.buildChart(chartData);
                            this.currentlyUpdating = false;
                        },
                        error => {
                            console.log(error);
                            this.currentlyUpdating = false;
                        },
                        () => {
                            this.currentlyUpdating = false;
                        },
                    );
            }
        }
    }

    updateChartSeries(chartData: ChartData, chartTypeChanged: boolean) {
        if (this.chart && !chartTypeChanged) {
            this.chartData = chartData;
            this.activeWidget.values.forEach((value, index) => {
                const seriesEntry = this.chart.series[index];
                if (seriesEntry && chartData.timeseries?.series?.[value]) {
                    const scaleFactor = this.activeWidget.scaleFactors?.[index] ?? 1;
                    const rawData = chartData.timeseries.series[value];
                    seriesEntry.data = scaleFactor !== 1
                        ? MainService.combineTimeData(chartData.timeseries.date, rawData.map(v => v != null ? v * scaleFactor : v))
                        : MainService.combineTimeData(chartData.timeseries.date, rawData);
                }
            });
            this.updateFlag = true;
        } else {
            this.buildChart(chartData);
        }
    }

    buildChart(chartData: ChartData) {
        this.chartData = chartData;
        this.chart = undefined;
        const series = [];
        const referenceKey = this.activeWidget.referenceX;
        const referenceData = referenceKey ? chartData.timeseries?.series?.[referenceKey] : undefined;
        const xAxisType = this.rpmMode ? undefined : 'datetime';
        const xAxisTitle = this.rpmMode ? referenceKey : 'timestamp';
        const pointFormat = this.rpmMode
            ? 'x: <b>{point.x}</b><br/>y: <b>{point.y}</b><br/>'
            : 'x: <b>{point.x:%Y-%m-%d %H:%M:%S.%L}</b><br/>y: <b>{point.y}</b><br/>';

        if (chartData.timeseries?.series) {
            this.activeWidget.values.forEach((value, index) => {
                if (chartData.timeseries.series[value] === undefined) return;
                if (this.rpmMode && value === referenceKey) return;

                const scaleFactor = this.activeWidget.scaleFactors?.[index] ?? 1;
                const rawData = chartData.timeseries.series[value];
                const scaledData = scaleFactor !== 1 ? rawData.map(v => v != null ? v * scaleFactor : v) : rawData;
                const designation = this.activeWidget.valuesDesignation?.[index] ?? value;

                series.push({
                    type: 'scatter',
                    name: designation,
                    visible: true,
                    data: this.rpmMode && referenceData
                        ? MainService.combineData(referenceData, scaledData)
                        : MainService.combineTimeData(chartData.timeseries.date, scaledData),
                });
            });
        }

        this.chart = {
            id: 'widgetChart',
            chart: {
                styledMode: true,
                zoomType: 'x',
                panning: true,
                panKey: 'shift',
                resetZoomButton: { position: { x: 0, y: -30 } },
                events: {
                    selection: function (event) {
                        if (this.rpmMode) {
                            this.highchartComponent.hideResetZoomButton();
                            return event.xAxis ? undefined : false;
                        }
                        if (event.xAxis) {
                            const startDate = new Date(Math.floor(event.xAxis[0].min));
                            const endDate = new Date(Math.ceil(event.xAxis[0].max));
                            this.highchartComponent.showResetZoomButton();
                            this.chartDateChange(startDate, endDate, false);
                        } else {
                            this.chartDateChange(this.chartDateRange.get('start').value, this.chartDateRange.get('end').value, false);
                            this.highchartComponent.hideResetZoomButton();
                        }
                    }.bind(this),
                    redraw: function () {
                        if (this.rpmMode) {
                            this.highchartComponent.hideResetZoomButton();
                        }
                    }.bind(this),
                },
            },
            plotOptions: {
                scatter: {
                    tooltip: {
                        pointFormat,
                    },
                },
                series: {
                    events: {
                        hide: function (event) {
                            this.updateChartSeriesVisibility(event.target.chart.series.filter(s => s.visible).map(s => s.name));
                        }.bind(this),
                        show: function (event) {
                            this.updateChartSeriesVisibility(event.target.chart.series.filter(s => s.visible).map(s => s.name));
                        }.bind(this),
                    },
                },
            },
            tooltip: {
                shared: true,
                useHTML: true,
                positioner() {
                    return { x: 0, y: 0 };
                },
            },
            credits: { enabled: false },
            legend: { enabled: true },
            title: { text: null },
            defs: {
                gradientWhiteTransparent: {
                    tagName: 'linearGradient',
                    id: 'gradientWhiteTransparent',
                    x1: 0,
                    y1: 0,
                    children: [{ tagName: 'stop', offset: 0 }, { tagName: 'stop', offset: 1 }],
                },
            },
            xAxis: [{ type: xAxisType, title: { enabled: true, text: xAxisTitle } }],
            yAxis: [{ title: { enabled: true, text: this.activeWidget.yAxisLabel || 'values' } }],
            series,
        };
    }

    toggleChartType() {
        this.rpmMode = !this.rpmMode;
        if (this.startDate !== undefined && this.endDate !== undefined) {
            this.chartDateChange(this.startDate, this.endDate, true);
        } else {
            this.setDefaultChartBasedOn24HoursBeforeLastConnected();
        }
    }

    last24Hours() {
        const endDate = new Date();
        const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 1, endDate.getHours(), endDate.getMinutes(), endDate.getSeconds());
        this.chartDateRange.patchValue({ start: startDate, end: endDate });
        this.chartDateChange(startDate, endDate, false);
    }

    lastWeek() {
        const endDate = new Date();
        const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 7, endDate.getHours(), endDate.getMinutes(), endDate.getSeconds());
        this.chartDateRange.patchValue({ start: startDate, end: endDate });
        this.chartDateChange(startDate, endDate, false);
    }

    lastMonth() {
        const endDate = new Date();
        const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 1, endDate.getDate(), endDate.getHours(), endDate.getMinutes(), endDate.getSeconds());
        this.chartDateRange.patchValue({ start: startDate, end: endDate });
        this.chartDateChange(startDate, endDate, false);
    }

    private updateChartSeriesVisibility(visibleSeries: string[]) {
        this.chart.series.forEach(s => {
            s.visible = visibleSeries.includes(s.name);
        });
    }

    private setZoomedTimeRange(start: Date, end: Date) {
        this.zoomedStartDate = start;
        this.zoomedEndDate = end;
        this.highchartComponent?.setSubtitle(`Zoomed to time range from ${start.toUTCString()} to ${end.toUTCString()}`);
    }

    private resetZoomedTimeRange() {
        this.zoomedStartDate = null;
        this.zoomedEndDate = null;
        this.highchartComponent?.setSubtitle();
    }

    ngOnDestroy(): void {
        if (this.routeParamSubscription) {
            this.routeParamSubscription.unsubscribe();
        }
        if (this.widgetChartSubscription) {
            this.widgetChartSubscription.unsubscribe();
        }
        this.chart = undefined;
        super.ngOnDestroy();
    }
}
